<template>
  <div class="yt-stack">
    <div class="yt-kpis">
      <yt-kpi-tile v-for="tile in tiles" :key="tile.key" :label="tile.label" :value="tile.value" :sub="tile.sub" :hint="tile.hint" :series="tile.series" :color="tile.color" :formatter="tile.formatter" />
    </div>

    <div class="yt-grid">
      <yt-chart-card
        class="yt-span-8"
        title="Impressions, clics et CTR"
        :subtitle="`Trois mesures, trois échelles, par ${granularityLabel}.`"
        :sources="['gsc']"
        :columns="panelColumns"
        :rows="buckets"
        :loading="!overview"
        :empty="!hasDiscover"
        empty-text="Aucune vidéo de ces filtres n'est passée par Discover sur la période."
      >
        <div class="yt-panels">
          <vue-apex-charts type="bar" height="150" :options="panelOptions('Impressions', fInt, 'bar')" :series="[{ name: 'Impressions', data: buckets.map((b) => b.discover_impressions) }]" />
          <vue-apex-charts type="bar" height="150" :options="panelOptions('Clics', fInt, 'bar')" :series="[{ name: 'Clics', data: buckets.map((b) => b.discover_clicks) }]" />
          <vue-apex-charts type="line" height="150" :options="panelOptions('CTR', (v) => fPct(v, 2), 'line')" :series="[{ name: 'CTR', data: buckets.map((b) => Number((b.discover_ctr * 100).toFixed(3))) }]" />
        </div>
      </yt-chart-card>

      <div class="yt-span-4 yt-stack">
        <yt-chart-card :title="`Discover par ${breakdownLevel}`" subtitle="Impressions, clics et CTR." :sources="['gsc']" :loading="loading" :empty="breakdownRows.length === 0" empty-text="Rien sur Discover pour ces filtres.">
          <table class="yt-table">
            <thead><tr><th>{{ breakdownLevelCap }}</th><th class="yt-table__num">Impressions</th><th class="yt-table__num">Clics</th><th class="yt-table__num">CTR</th></tr></thead>
            <tbody>
              <tr v-for="row in breakdownRows" :key="row.id">
                <td>{{ row.label }}</td>
                <td class="yt-table__num">{{ fBig(row.discover_impressions) }}</td>
                <td class="yt-table__num">{{ fBig(row.discover_clicks) }}</td>
                <td class="yt-table__num">{{ fPct(row.discover_ctr, 2) }}</td>
              </tr>
            </tbody>
          </table>
        </yt-chart-card>
        <yt-chart-card title="Passées par Discover ou non" subtitle="Vues moyennes sur la période des vidéos publiées sur la période." :sources="['gsc', 'yt']" :loading="loading" :empty="!comparison">
          <div v-if="comparison" class="yt-compare">
            <div class="yt-compare__item yt-compare__item--on">
              <span class="yt-compare__k">Passées par Discover</span>
              <b>{{ fInt(comparison.with_discover.avg_views) }}</b>
              <span class="yt-compare__n">{{ fInt(comparison.with_discover.videos) }} vidéo(s)</span>
            </div>
            <div class="yt-compare__item">
              <span class="yt-compare__k">Les autres</span>
              <b>{{ fInt(comparison.without_discover.avg_views) }}</b>
              <span class="yt-compare__n">{{ fInt(comparison.without_discover.videos) }} vidéo(s)</span>
            </div>
          </div>
        </yt-chart-card>
      </div>

      <yt-chart-card class="yt-span-12" title="Vidéos passées par Discover" subtitle="Triées par impressions ; cliquez sur une ligne pour ouvrir la fiche." :sources="['gsc', 'yt', 'calc']" :loading="false">
        <v-data-table
          :headers="headers"
          :items="videos"
          :options.sync="tableOptions"
          :server-items-length="total"
          :loading="tableLoading"
          :footer-props="{ 'items-per-page-options': [10, 25, 50], 'items-per-page-text': 'Lignes par page' }"
          class="yt-data-table"
          dense
          @click:row="(row) => $emit('open-video', row.video_id)"
        >
          <template #item.title="{ item }">
            <div class="yt-cell-video">
              <img v-if="item.thumbnail_url" :src="item.thumbnail_url" alt="" loading="lazy" />
              <div>
                <div class="yt-cell-video__t">{{ item.title }}</div>
                <div class="yt-cell-video__m">{{ item.site_name }} · {{ fDayLong(item.published_at) }}</div>
              </div>
            </div>
          </template>
          <template #item.discover_impressions="{ item }">{{ fInt(item.discover_impressions) }}</template>
          <template #item.discover_clicks="{ item }">{{ fInt(item.discover_clicks) }}</template>
          <template #item.discover_ctr="{ item }">{{ fPct(item.discover_ctr, 2) }}</template>
          <template #item.discover_views="{ item }">{{ fInt(item.discover_views) }}</template>
          <template #item.discover_revenue="{ item }">{{ fEur(item.discover_revenue) }}</template>
          <template #item.views="{ item }">{{ fInt(item.views) }}</template>
          <template #item.avg_duration="{ item }">{{ fDuration(item.avg_duration) }}</template>
          <template #item.likes="{ item }">{{ fInt(item.likes) }}</template>
          <template #item.dislikes="{ item }">{{ fInt(item.dislikes) }}</template>
          <template #item.comments="{ item }">{{ fInt(item.comments) }}</template>
          <template #item.shares="{ item }">{{ fInt(item.shares) }}</template>
        </v-data-table>
      </yt-chart-card>

      <yt-discover-import-card class="yt-span-12" :active="active" />
    </div>
  </div>
</template>

<script>
import { computed, ref, toRefs, watch } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import YtChartCard from '../YtChartCard.vue'
import YtKpiTile from '../YtKpiTile.vue'
import YtDiscoverImportCard from '../YtDiscoverImportCard.vue'
import useYoutubePerf from '../useYoutubePerf'
import { COLORS, GRANULARITY_LABELS, baseChartOptions, bucketLabel, bucketTitle, fBig, fCompact, fDayLong, fDuration, fEur, fInt, fPct } from '../ytFormat'

const HEADERS = [
  { text: 'Vidéo', value: 'title', sortable: true },
  { text: 'Impressions', value: 'discover_impressions', align: 'end' },
  { text: 'Clics', value: 'discover_clicks', align: 'end' },
  { text: 'CTR', value: 'discover_ctr', align: 'end' },
  { text: 'Vues Discover', value: 'discover_views', align: 'end' },
  { text: 'Revenus Discover', value: 'discover_revenue', align: 'end' },
  { text: 'Vues totales', value: 'views', align: 'end' },
  { text: 'Durée moy.', value: 'avg_duration', align: 'end' },
  { text: 'Likes', value: 'likes', align: 'end' },
  { text: 'Dislikes', value: 'dislikes', align: 'end' },
  { text: 'Comm.', value: 'comments', align: 'end' },
  { text: 'Partages', value: 'shares', align: 'end' },
]

export default {
  name: 'YtDiscoverTab',
  components: { VueApexCharts, YtChartCard, YtKpiTile, YtDiscoverImportCard },
  props: { active: { type: Boolean, default: false } },
  setup(props) {
    const { active } = toRefs(props)
    const { store, get, ensureOverview, useTabLoader, filtersVersion } = useYoutubePerf()

    const discover = ref(null)
    const loading = ref(false)
    const videos = ref([])
    const total = ref(0)
    const tableLoading = ref(false)
    const tableOptions = ref({ page: 1, itemsPerPage: 10, sortBy: ['discover_impressions'], sortDesc: [true] })

    const fetchDiscover = async () => {
      loading.value = true
      try {
        const { data } = await get('/discover')
        discover.value = data
      } finally {
        loading.value = false
      }
    }
    const fetchVideos = async () => {
      tableLoading.value = true
      try {
        const options = tableOptions.value
        const { data } = await get('/discover/videos', {
          sort_by: options.sortBy?.[0] || 'discover_impressions',
          sort_desc: options.sortDesc?.[0] !== false,
          page: options.page || 1,
          limit: options.itemsPerPage || 10,
        })
        videos.value = data.items || []
        total.value = data.total || 0
      } finally {
        tableLoading.value = false
      }
    }
    useTabLoader(active, async () => {
      tableOptions.value = { ...tableOptions.value, page: 1 }
      await Promise.all([ensureOverview(), fetchDiscover(), fetchVideos()])
    })
    watch(tableOptions, () => { if (active.value) fetchVideos() }, { deep: true })

    const overview = computed(() => store.getters['youtubePerf/getOverview'])
    const buckets = computed(() => overview.value?.series?.buckets || [])
    const granularity = computed(() => overview.value?.series?.granularity || 'day')
    const granularityLabel = computed(() => GRANULARITY_LABELS[granularity.value])
    const summary = computed(() => discover.value?.summary || null)
    const hasDiscover = computed(() => (summary.value?.discover_impressions || 0) > 0)
    const categories = computed(() => buckets.value.map((b) => bucketLabel(b, granularity.value)))
    const titles = computed(() => buckets.value.map((b) => bucketTitle(b, granularity.value)))
    const seriesOf = (key) => buckets.value.map((b) => Number(b[key] || 0))

    const tiles = computed(() => {
      const s = summary.value
      if (!s) return []
      return [
        { key: 'impressions', label: 'Impressions Discover', value: fBig(s.discover_impressions), series: seriesOf('discover_impressions'), color: COLORS.discover, formatter: fInt },
        { key: 'clicks', label: 'Clics Discover', value: fBig(s.discover_clicks), series: seriesOf('discover_clicks'), color: COLORS.discover, formatter: fInt },
        { key: 'ctr', label: 'CTR Discover', value: fPct(s.discover_ctr, 2), hint: 'Clics ÷ impressions', series: seriesOf('discover_ctr'), color: COLORS.discover, formatter: (v) => fPct(v, 2) },
        { key: 'views', label: 'Vues venues de Discover', value: fBig(s.discover_views), sub: `${fPct(s.discover_views_share)} des vues (estimation)`, series: seriesOf('discover_views'), color: COLORS.views, formatter: fInt },
        { key: 'revenue', label: 'Revenus venus de Discover', value: fEur(s.discover_revenue), sub: `${fPct(s.discover_revenue_share)} des revenus (estimation)`, series: seriesOf('discover_revenue'), color: COLORS.revenue, formatter: fEur },
        { key: 'videos', label: 'Vidéos passées par Discover', value: fInt(s.videos_with_discover), sub: `dont ${fInt(s.published_with_discover)} des ${fInt(s.published)} publiées (${fPct(s.published_with_discover_share)})`, series: [], color: COLORS.discover, formatter: fInt },
      ]
    })

    const panelOptions = (name, formatter, type) => ({
      ...baseChartOptions(),
      chart: { ...baseChartOptions().chart, type },
      colors: [COLORS.discover],
      title: { text: name, style: { fontSize: '12px', fontWeight: 600, color: '#475569' } },
      stroke: { width: type === 'line' ? 2 : 0, curve: 'smooth' },
      plotOptions: { bar: { borderRadius: 3, columnWidth: buckets.value.length > 40 ? '90%' : '60%' } },
      xaxis: { ...baseChartOptions().xaxis, categories: categories.value, labels: { show: false } },
      yaxis: { labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (v) => (type === 'line' ? `${Number(v).toFixed(1)} %` : fCompact(v)) }, min: 0, forceNiceScale: true },
      tooltip: { x: { formatter: (_, { dataPointIndex }) => titles.value[dataPointIndex] || '' }, y: { formatter: (v) => (type === 'line' ? formatter(v / 100) : formatter(v)) } },
    })
    const panelColumns = [
      { key: 'label', label: 'Période', numeric: false, format: (_, row) => bucketTitle(row, granularity.value) },
      { key: 'discover_impressions', label: 'Impressions', format: fInt },
      { key: 'discover_clicks', label: 'Clics', format: fInt },
      { key: 'discover_ctr', label: 'CTR', format: (v) => fPct(v, 2) },
    ]

    const breakdownRows = computed(() => (discover.value?.breakdown?.rows || []).filter((row) => row.discover_impressions > 0).sort((a, b) => b.discover_impressions - a.discover_impressions))
    const breakdownLevel = computed(() => (discover.value?.breakdown?.level === 'site' ? 'site' : 'univers'))
    const breakdownLevelCap = computed(() => (breakdownLevel.value === 'site' ? 'Site' : 'Univers'))
    const comparison = computed(() => discover.value?.comparison || null)

    return {
      overview, buckets, granularityLabel, hasDiscover, loading, tiles, panelOptions, panelColumns,
      breakdownRows, breakdownLevel, breakdownLevelCap, comparison,
      headers: HEADERS, videos, total, tableLoading, tableOptions, filtersVersion,
      fBig, fInt, fEur, fPct, fDayLong, fDuration,
    }
  },
}
</script>

<style lang="scss" scoped>
.yt-stack { display: flex; flex-direction: column; gap: $rmr-space-lg; min-width: 0; }
.yt-kpis {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: $rmr-space-sm;
  @media (max-width: 1180px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.yt-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: $rmr-space-lg; }
.yt-span-4 { grid-column: span 4; }
.yt-span-8 { grid-column: span 8; }
.yt-span-12 { grid-column: span 12; }
@media (max-width: 1100px) { .yt-span-4, .yt-span-8 { grid-column: span 12; } }
.yt-panels { display: flex; flex-direction: column; gap: 2px; }

.yt-compare { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $rmr-space-sm; }
.yt-compare__item {
  display: flex; flex-direction: column; gap: 2px; padding: 12px; border-radius: $rmr-radius-lg; background: $rmr-surface-2;
  b { font-size: 24px; font-weight: 800; color: $rmr-text-primary; letter-spacing: -0.02em; }
  &--on { background: $rmr-indigo-bg; b { color: $rmr-indigo-dark; } }
}
.yt-compare__k { font-size: $rmr-fs-sm; color: $rmr-text-secondary; font-weight: 600; }
.yt-compare__n { font-size: $rmr-fs-xs; color: $rmr-text-subtle; }
</style>

<style lang="scss">
.yt-data-table {
  tbody tr { cursor: pointer; }
  th { white-space: nowrap; }
  td { font-variant-numeric: tabular-nums; white-space: nowrap; }
}
.yt-cell-video {
  display: flex; align-items: center; gap: 10px; min-width: 260px; max-width: 460px; padding: 4px 0;
  img { width: 56px; height: 32px; object-fit: cover; border-radius: 5px; flex: none; background: $rmr-surface-2; }
}
.yt-cell-video__t { font-weight: 600; color: $rmr-text-primary; white-space: normal; line-height: 1.25; }
.yt-cell-video__m { font-size: $rmr-fs-xs; color: $rmr-text-subtle; }
</style>
